import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type {
    Blocks,
    ContentBlock,
    ListGroups,
    ListItem,
    Offer,
    Settings,
    SiteFrame
} from '@/types/content'

import { ArrowUpRight, CalendarDays, Mail, MessageCircle, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { Layout } from '@/components/generals/layout'
import { SectionIntro } from '@/components/generals/section-intro'
import { Seo } from '@/components/generals/seo'
import { InquiryForm } from '@/components/sections/inquiry-form'

import {
    cms,
    hasValue,
    padNumber,
    toExternalHref,
    toTelHref,
    toWhatsAppHref
} from '@/lib/cms'

import { REVALIDATE_SECONDS, getOffers, getPageContent } from '@/lib/content'

interface ContactPageProps {
    frame: SiteFrame
    blocks: Blocks
    groups: ListGroups
    offers: Array<Offer>
}

interface BlockHeadingProps {
    block?: ContentBlock
}

// Small label, heading and optional line of text for the side columns of the contact page.
const BlockHeading: FC<BlockHeadingProps> = ({ block }): ReactNode => {
    if (!block) return null

    return (
        <div className='flex flex-col gap-3'>
            {hasValue(block.dachzeile) && (
                <p className='type-eyebrow flex items-center gap-3 text-ember'>
                    <span aria-hidden='true' className='h-px w-8 bg-current' />
                    <span data-mdlfy-cms={cms('inhalte', block.id, 'dachzeile')}>{block.dachzeile}</span>
                </p>
            )}

            {hasValue(block.ueberschrift) && (
                <h2 data-mdlfy-cms={cms('inhalte', block.id, 'ueberschrift')} className='text-xl font-semibold tracking-[-0.01em] text-ink'>
                    {block.ueberschrift}
                </h2>
            )}

            {hasValue(block.text) && (
                <p data-mdlfy-cms={cms('inhalte', block.id, 'text')} className='text-[0.9375rem] leading-relaxed text-smoke'>
                    {block.text}
                </p>
            )}
        </div>
    )
}

interface NextStepsProps {
    block?: ContentBlock
    steps: Array<ListItem>
}

const NextSteps: FC<NextStepsProps> = ({ block, steps }): ReactNode => {
    if (steps.length === 0) return null

    return (
        <div className='flex flex-col gap-6'>
            <BlockHeading block={block} />

            <ol className='flex flex-col'>
                {steps.map((step, index) => (
                    <li key={step.id} className='grid grid-cols-[2.5rem_1fr] gap-4 border-t border-ink/12 py-5'>
                        <span aria-hidden='true' className='type-label pt-1 text-smoke'>
                            {padNumber(index)}
                        </span>

                        <div className='flex flex-col gap-1.5'>
                            <h3 data-mdlfy-cms={cms('listenpunkte', step.id, 'titel')} className='font-semibold text-ink'>
                                {step.titel}
                            </h3>

                            {hasValue(step.text) && (
                                <p data-mdlfy-cms={cms('listenpunkte', step.id, 'text')} className='text-[0.9375rem] leading-relaxed text-smoke'>
                                    {step.text}
                                </p>
                            )}
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    )
}

interface DirectContactProps {
    block?: ContentBlock
    settings: Settings
}

const contactLinkClass = 'group flex items-center gap-4 border-t border-ink/12 py-4 text-[0.9375rem] font-medium text-ink transition-colors duration-300 hover:text-ember'

// Email, phone, WhatsApp and an optional booking link, each shown once it is set in the CMS settings.
const DirectContact: FC<DirectContactProps> = ({ block, settings }): ReactNode => {
    const hasAny = hasValue(settings.email) || hasValue(settings.telefon) || hasValue(settings.whatsapp) || hasValue(settings.calendly)

    return (
        <div className='flex flex-col gap-6'>
            <BlockHeading block={block} />

            {hasAny
                ? (
                    <ul className='flex flex-col border-b border-ink/12'>
                        {hasValue(settings.email) && (
                            <li>
                                <a href={`mailto:${settings.email}`} className={contactLinkClass}>
                                    <Mail aria-hidden='true' className='size-5 shrink-0 text-smoke transition-colors duration-300 group-hover:text-ember' strokeWidth={1.75} />
                                    <span data-mdlfy-cms={cms('einstellungen', settings.id, 'email')} className='break-all'>{settings.email}</span>
                                </a>
                            </li>
                        )}

                        {hasValue(settings.telefon) && (
                            <li>
                                <a href={toTelHref(settings.telefon)} className={contactLinkClass}>
                                    <Phone aria-hidden='true' className='size-5 shrink-0 text-smoke transition-colors duration-300 group-hover:text-ember' strokeWidth={1.75} />
                                    <span data-mdlfy-cms={cms('einstellungen', settings.id, 'telefon')}>{settings.telefon}</span>
                                </a>
                            </li>
                        )}

                        {hasValue(settings.whatsapp) && (
                            <li>
                                <a href={toWhatsAppHref(settings.whatsapp)} target='_blank' rel='noopener noreferrer' className={contactLinkClass}>
                                    <MessageCircle aria-hidden='true' className='size-5 shrink-0 text-smoke transition-colors duration-300 group-hover:text-ember' strokeWidth={1.75} />
                                    <span>WhatsApp</span>
                                    <ArrowUpRight aria-hidden='true' className='ml-auto size-4 transition-transform duration-500 ease-brand group-hover:rotate-45' />
                                </a>
                            </li>
                        )}
                    </ul>
                )
                : (
                    <ul className='flex flex-col gap-2 text-[0.9375rem] text-smoke'>
                        <li className='flex'>
                            <span data-mdlfy-cms={cms('einstellungen', settings.id, 'email')} data-empty-hint='E-Mail-Adresse in den Einstellungen ergänzen' className='is-placeholder' />
                        </li>
                        <li className='flex'>
                            <span data-mdlfy-cms={cms('einstellungen', settings.id, 'telefon')} data-empty-hint='Telefonnummer in den Einstellungen ergänzen' className='is-placeholder' />
                        </li>
                    </ul>
                )}

            {hasValue(settings.calendly) && block && hasValue(block.button_text) && (
                <div className='flex items-center gap-3'>
                    <CalendarDays aria-hidden='true' className='size-5 text-smoke' strokeWidth={1.75} />
                    <ButtonLink
                        href={toExternalHref(settings.calendly)}
                        label={block.button_text}
                        labelCms={cms('inhalte', block.id, 'button_text')}
                        variant='secondary'
                    />
                </div>
            )}
        </div>
    )
}

const ContactPage: FC<ContactPageProps> = ({ frame, blocks, groups, offers }): ReactNode => {
    const seo = blocks['kontakt.seo']

    return (
        <Layout frame={frame} showClosing={false} showMobileCta={false}>
            <Seo
                title={seo?.ueberschrift ?? 'Erstgespräch anfragen'}
                description={seo?.text ?? undefined}
                siteUrl={frame.settings.webseite}
                path='/kontakt'
            />

            <section aria-labelledby='seite-titel' className='relative bg-bone pb-24 pt-14 sm:pb-32 sm:pt-20 lg:pt-24'>
                <Container className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                    <div className='flex flex-col gap-14 lg:col-span-5'>
                        <SectionIntro block={blocks['kontakt.hero']} as='h1' size='xl' headingId='seite-titel' motion='entrance' />

                        <div className='hidden flex-col gap-14 lg:flex'>
                            <NextSteps block={blocks['kontakt.schritte']} steps={groups['kontakt.schritte'] ?? []} />
                            <DirectContact block={blocks['kontakt.direkt']} settings={frame.settings} />
                        </div>
                    </div>

                    <div className='lg:col-span-7'>
                        <div
                            id='formular'
                            className={cn(
                                'relative rounded-[calc(var(--radius)*2)] border border-ink/10 bg-paper p-6 shadow-[0_1px_0_rgba(21,18,15,0.04),0_30px_60px_-40px_rgba(21,18,15,0.35)] sm:p-10',
                                'animate-in fade-in slide-in-from-bottom-3 fill-mode-both delay-200 duration-1000 ease-brand'
                            )}
                        >
                            <InquiryForm
                                settings={frame.settings}
                                offers={offers}
                                goals={groups.ziele ?? []}
                                success={blocks['kontakt.erfolg']}
                            />
                        </div>
                    </div>

                    <div className='flex flex-col gap-14 lg:hidden'>
                        <NextSteps block={blocks['kontakt.schritte']} steps={groups['kontakt.schritte'] ?? []} />
                        <DirectContact block={blocks['kontakt.direkt']} settings={frame.settings} />
                    </div>
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ContactPageProps> = async () => {
    const [content, offers] = await Promise.all([
        getPageContent({
            blocks: ['kontakt.seo', 'kontakt.hero', 'kontakt.schritte', 'kontakt.direkt', 'kontakt.erfolg'],
            groups: ['kontakt.schritte', 'ziele']
        }),
        getOffers()
    ])

    return {
        props: {
            frame: content.frame,
            blocks: content.blocks,
            groups: content.groups,
            offers
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default ContactPage
